"use client";
import React, { createContext, useContext, useState, useEffect } from "react";
import { getHotelData, getSearchData } from "@/app/utils/apis/apiService";
import { HotelDetail, SearchData } from "@/app/utils/apis/types";
import { RoomOffer } from "@/app/utils/services/model/roomOffer";
import { transformImages } from "@/app/components/pages/Results/components/utils";
import axios from "axios";
import { getPartnerCode } from "@/app/utils/services/service/localStorageService";
import { useLocale } from "next-intl";

type ContextType = {
  searchData: SearchData | null;
  hotelData: HotelDetail | null;
  roomOffers: RoomOffer[] | null;
  searchOfferDataError: Error | null;
  hotelDataLoading: boolean;
  searchDataLoading: boolean;
  singleOfferLoading: boolean;
  roomOffersLoading: boolean;
  searchId: string | null;
  hotelId: string | null;
  tokenId: string | null;
  providerHotelId: string | null;
  setHotelId: (id: string) => void;
  setConnectedWallet: (type: string) => void;
  setSearchId: (id: string | null) => void;
  setProviderOfferId: (id: string | null) => void;
  setProviderHotelId: (id: string | null) => void;
  setTokenId: (id: string) => void;
  setWalletId: (id: string | null) => void;
  setSearchData: (data: SearchData | null) => void;
  setRoomOffers: (data: RoomOffer[] | null) => void;
  setRoomOffersLoading: (data: boolean) => void;
  setSingleOfferLoading: (data: boolean) => void;
  setHotelDataLoading: (loading: boolean) => void;
  setHotelData: (data: HotelDetail | null) => void;
  setSearchDataLoading: (loading: boolean) => void;
};

const SearchOfferDataContext = createContext<ContextType>({
  searchData: null,
  hotelData: null,
  roomOffers: null,
  searchOfferDataError: null,
  hotelDataLoading: true,
  searchDataLoading: true,
  singleOfferLoading: true,
  roomOffersLoading: false,
  searchId: null,
  tokenId: null,
  setConnectedWallet: () => {},
  setHotelId: () => {},
  setSearchId: () => {},
  setProviderOfferId: () => {},
  setProviderHotelId: () => {},
  providerHotelId: null,
  hotelId: null,
  setTokenId: () => {},
  setWalletId: () => {},
  setSearchData: () => {},
  setRoomOffers: () => {},
  setRoomOffersLoading: () => {},
  setSingleOfferLoading: () => {},
  setHotelDataLoading: () => {},
  setHotelData: () => {},
  setSearchDataLoading: () => {},
});

export const SearchOfferDataProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const locale = useLocale();
  const [hotelData, setHotelData] = useState<HotelDetail | null>(null);
  const [roomOffers, setRoomOffers] = useState<RoomOffer[] | null>(null);
  const [searchData, setSearchData] = useState<SearchData | null>(null);
  const [searchOfferDataError, setSearchOfferDataError] =
    useState<Error | null>(null);
  const [hotelDataLoading, setHotelDataLoading] = useState(true);
  const [searchDataLoading, setSearchDataLoading] = useState(true);
  const [singleOfferLoading, setSingleOfferLoading] = useState(true);
  const [roomOffersLoading, setRoomOffersLoading] = useState(false);

  const [tokenId, setTokenId] = useState<string | null>(null);
  const [hotelId, setHotelId] = useState<string | null>(null);
  const [searchId, setSearchId] = useState<string | null>(null);
  const [providerOfferId, setProviderOfferId] = useState<string | null>(null);
  const [providerHotelId, setProviderHotelId] = useState<string | null>(null);
  const [walletId, setWalletId] = useState<string | null>(null);
  const [connectedWallet, setConnectedWallet] = useState<string | null>(null);

  useEffect(() => {
    async function fetchOfferData() {
      if (!providerOfferId || !providerHotelId) return;

      setRoomOffersLoading(true);
      setSingleOfferLoading(true);
      const body = {
        id: providerHotelId,
        searchId,
        tokenId,
        walletId,
        partnerCode: getPartnerCode(),
        connectedWallet,
      };
      const { data } = await axios.post(
        `/api/single-property-search?lang=${locale}`,
        body
      );
      setRoomOffers(data.roomOffers ?? []);
      setSingleOfferLoading(false);
      setRoomOffersLoading(false);
    }

    fetchOfferData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerOfferId, providerHotelId, searchData]);

  useEffect(() => {
    async function fetchSearchData() {
      if ((tokenId && walletId) || searchId) {
        setSearchDataLoading(true);
        const { data, error } = await getSearchData(
          locale,
          tokenId,
          walletId,
          searchId
        );
        if (error) {
          setSearchOfferDataError(error);
        } else {
          setSearchData(data);
        }
        setSearchDataLoading(false);
      }
    }

    fetchSearchData();
  }, [tokenId, walletId, searchId]);

  useEffect(() => {
    async function fetchHotelData() {
      if (!hotelId) return;
      setHotelDataLoading(true);
      const { data, error } = await getHotelData(locale, hotelId);
      if (error) {
        setSearchOfferDataError(error);
      } else {
        const images = transformImages(data?.images || []);
        const hotelDetails: any = { ...data, images };
        setHotelData(hotelDetails);
      }
      setHotelDataLoading(false);
    }

    fetchHotelData();
  }, [hotelId]);

  return (
    <SearchOfferDataContext.Provider
      value={{
        hotelData,
        roomOffers,
        searchData,
        searchOfferDataError,
        hotelDataLoading,
        searchDataLoading,
        roomOffersLoading,
        singleOfferLoading,
        searchId,
        setHotelId,
        hotelId,
        tokenId,
        setConnectedWallet,
        providerHotelId,
        setSearchId,
        setProviderOfferId,
        setProviderHotelId,
        setTokenId,
        setWalletId,
        setSearchData,
        setRoomOffers,
        setRoomOffersLoading,
        setSingleOfferLoading,
        setHotelData,
        setHotelDataLoading,
        setSearchDataLoading,
      }}
    >
      {children}
    </SearchOfferDataContext.Provider>
  );
};

export const useSearchOfferData = () => useContext(SearchOfferDataContext);
